import { onReady } from '@/helpers/dom';

const SELECTOR_ROOT = '.lmb-faq';
const SELECTOR_INNER = '.lmb-faq-inner';
const SELECTOR_QUESTION = '.lmb-q';
const SELECTOR_ANSWER = '.lmb-a';
const SELECTOR_TRIGGER = '.lmb-faq-trigger';
const SELECTOR_ITEM = '.lmb-faq-item';

const READY_ATTRIBUTE = 'data-faq';
const OPEN_CLASS = 'is-open';

type TGroup = {
    question: HTMLElement;
    answers: HTMLElement[];
};

let sequence = 0;

function uniqueId(prefix: string): string {
    let id: string;

    do {
        sequence += 1;
        id = `${prefix}-${sequence}`;
    } while (document.getElementById(id));

    return id;
}

function collectGroups(inner: HTMLElement): TGroup[] {
    const groups: TGroup[] = [];

    Array.from(inner.children).forEach((child) => {
        if (!(child instanceof HTMLElement)) {
            return;
        }

        if (child.matches(SELECTOR_QUESTION)) {
            groups.push({ question: child, answers: [] });

            return;
        }

        const current = groups[groups.length - 1];
        if (current && child.matches(SELECTOR_ANSWER)) {
            current.answers.push(child);
        }
    });

    return groups.filter((group) => group.answers.length > 0);
}

function buildItem(inner: HTMLElement, group: TGroup): void {
    const { question, answers } = group;

    const item = document.createElement('div');
    item.className = 'lmb-faq-item';
    inner.insertBefore(item, question);

    const triggerId = uniqueId('lmb-faq-trigger');
    const panelId = uniqueId('lmb-faq-panel');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.id = triggerId;
    trigger.className = 'lmb-faq-trigger';
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', panelId);

    const label = document.createElement('span');
    label.className = 'lmb-faq-label';
    while (question.firstChild) {
        label.appendChild(question.firstChild);
    }
    trigger.appendChild(label);

    const icon = document.createElement('span');
    icon.className = 'lmb-faq-icon';
    icon.setAttribute('aria-hidden', 'true');
    trigger.appendChild(icon);

    const panel = document.createElement('div');
    panel.id = panelId;
    panel.className = 'lmb-faq-panel';
    panel.setAttribute('role', 'region');
    panel.setAttribute('aria-labelledby', triggerId);

    const panelInner = document.createElement('div');
    panelInner.className = 'lmb-faq-panel-inner';
    answers.forEach((answer) => panelInner.appendChild(answer));
    panel.appendChild(panelInner);

    question.classList.add('lmb-faq-heading');
    question.appendChild(trigger);

    item.appendChild(question);
    item.appendChild(panel);
}

function setOpen(item: HTMLElement, open: boolean): void {
    item.classList.toggle(OPEN_CLASS, open);
    item
        .querySelector(SELECTOR_TRIGGER)
        ?.setAttribute('aria-expanded', String(open));
}

function bindToggle(root: HTMLElement): void {
    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const trigger = target.closest(SELECTOR_TRIGGER);
        const item = trigger?.closest(SELECTOR_ITEM);
        if (!(item instanceof HTMLElement) || !root.contains(item)) {
            return;
        }

        const open = !item.classList.contains(OPEN_CLASS);

        root.querySelectorAll(`${SELECTOR_ITEM}.${OPEN_CLASS}`).forEach(
            (opened) => {
                if (opened instanceof HTMLElement) {
                    setOpen(opened, false);
                }
            },
        );

        if (open) {
            setOpen(item, true);
        }
    });
}

function enhance(root: HTMLElement): void {
    if (root.hasAttribute(READY_ATTRIBUTE)) {
        return;
    }

    const inner = root.querySelector<HTMLElement>(SELECTOR_INNER) ?? root;
    const groups = collectGroups(inner);

    if (groups.length === 0) {
        return;
    }

    inner.querySelectorAll('.lmb-icon').forEach((el) => el.remove());

    groups.forEach((group) => buildItem(inner, group));
    bindToggle(root);

    root.setAttribute(READY_ATTRIBUTE, 'ready');
}

function Faq(): void {
    onReady(() => {
        document.querySelectorAll<HTMLElement>(SELECTOR_ROOT).forEach(enhance);
    });
}

export { Faq };
